import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star, ArrowRight } from 'lucide-react';

interface PricingSectionProps {
  onTrainerStartClick?: () => void;
}

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    aria-hidden
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M12.04 2C6.59 2 2.2 6.26 2.2 11.58c0 1.96.57 3.77 1.56 5.31L2 22l5.29-1.71a10.2 10.2 0 0 0 4.75 1.2h.01c5.45 0 9.84-4.26 9.84-9.58C21.89 6.26 17.49 2 12.04 2zm0 17.45h-.01a8.3 8.3 0 0 1-4.23-1.16l-.3-.18-3.14 1.02 1.03-3.04-.2-.31a7.7 7.7 0 0 1-1.2-4.19c0-4.24 3.48-7.69 7.76-7.69 4.27 0 7.74 3.45 7.74 7.69 0 4.24-3.47 7.68-7.75 7.68zm4.26-5.77c-.23-.11-1.36-.67-1.57-.75-.21-.08-.36-.11-.5.11-.15.23-.57.75-.7.9-.13.15-.26.17-.49.06-.23-.11-.97-.37-1.85-1.17-.68-.61-1.15-1.37-1.28-1.6-.13-.23-.01-.35.1-.46.1-.1.23-.26.34-.4.11-.15.15-.23.23-.38.08-.15.04-.28-.02-.39-.06-.11-.5-1.2-.69-1.64-.18-.44-.37-.38-.5-.39h-.43c-.15 0-.39.06-.59.28-.2.23-.78.76-.78 1.85 0 1.08.8 2.13.91 2.28.11.15 1.57 2.45 3.84 3.34.54.23.96.36 1.29.46.54.17 1.03.14 1.42.08.43-.07 1.36-.55 1.55-1.08.19-.53.19-.99.13-1.08-.06-.09-.21-.15-.44-.26z"
    />
  </svg>
);

const PricingSection: React.FC<PricingSectionProps> = ({ onTrainerStartClick }) => {
  const whatsappUrl =
    'https://wa.me/5493512392367?text=Quiero%20consultar%20por%20la%20licencia%20institucional%20de%20%2BTrainer';

  return (
    <section id="licencias" className="section-padding bg-white">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-mas10-dark mb-4">
            Licencias pensadas para profesionales e instituciones
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Empezá a usar +Trainer como entrenador individual o llevá la plataforma a todo tu club con una
            experiencia personalizada.
          </p>
        </motion.div>

        {/* Pricing cards */}
        <div className="grid gap-8 lg:grid-cols-2">
          {/* Entrenador */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative bg-gray-50 border border-gray-200 rounded-3xl p-8 md:p-10 shadow-sm flex flex-col"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl md:text-3xl font-display font-bold text-mas10-dark">
                Licencia Entrenador
              </h3>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-mas10-accent/20 text-mas10-dark text-xs font-semibold uppercase tracking-wide">
                <Star className="w-3 h-3" />
                Ideal para profesionales
              </span>
            </div>

            <p className="text-gray-600 mb-6">
              Para entrenadores que quieren <strong>planificar</strong> mejor, <strong>monitorear</strong> la carga y{' '}
              <strong>prevenir</strong> lesiones en sus deportistas, usando todo el <strong>motor tecnológico</strong>{' '}
              de +Trainer.
            </p>

            <div className="flex flex-wrap items-baseline gap-4 mb-6">
              <div>
                <div className="text-sm uppercase tracking-[0.18em] text-gray-500 mb-1">Mensual</div>
                <div className="flex items-end gap-1">
                  <span className="text-3xl md:text-4xl font-display font-bold text-mas10-dark">
                    US$ 9,99
                  </span>
                  <span className="text-sm text-gray-500 mb-1">/ mes</span>
                </div>
              </div>
              <div className="h-10 w-px bg-gray-200 hidden sm:block" />
              <div>
                <div className="inline-flex items-center gap-2 mb-1">
                  <span className="text-sm uppercase tracking-[0.18em] text-gray-500">Anual</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                    2 meses bonificados
                  </span>
                </div>
                <div className="flex items-end gap-1">
                  <span className="text-3xl md:text-4xl font-display font-bold text-mas10-dark">
                    US$ 99,99
                  </span>
                  <span className="text-sm text-gray-500 mb-1">/ año</span>
                </div>
              </div>
            </div>

            <ul className="space-y-2 text-sm md:text-base text-gray-700 mb-8">
              {[
                <>
                  Planificación de entrenamientos con{' '}
                  <strong>biblioteca audiovisual de ejercicios</strong>.
                </>,
                <>
                  <strong>Monitoreo de carga interna y externa</strong> de entrenamiento.
                </>,
                <>
                  Herramientas para <strong>prevención de lesiones basadas en la evidencia</strong>.
                </>,
                <>
                  <strong>Gestión de atletas</strong> y{' '}
                  <strong>seguimiento de progreso en un solo lugar</strong>.
                </>,
              ].map((item, index) => (
                <li key={index} className="flex gap-2">
                  <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-mas10-accent/30 text-mas10-dark">
                    <Check className="w-3 h-3" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <motion.button
              type="button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => onTrainerStartClick?.()}
              className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-mas10-primary hover:bg-blue-800 text-white font-semibold text-sm md:text-base shadow-md transition-colors self-start"
            >
              Empezar como entrenador
              <ArrowRight className="w-4 h-4 ml-2" />
            </motion.button>
          </motion.div>

          {/* Instituciones */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="relative bg-gradient-to-br from-mas10-primary via-blue-700 to-mas10-secondary rounded-3xl p-8 md:p-10 text-white shadow-xl flex flex-col"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl md:text-3xl font-display font-bold">
                Instituciones con identidad propia
              </h3>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/15 text-xs font-semibold uppercase tracking-wide">
                <Star className="w-3 h-3" />
                Personalizado
              </span>
            </div>

            <p className="text-white/90 mb-6">
              Convertí +Trainer en la app oficial de tu club, academia o institución:{' '}
              <strong>colores, escudo, dominios y gestión centralizada</strong> para todo tu staff técnico.
            </p>

            <div className="mb-6">
              <div className="text-sm uppercase tracking-[0.18em] text-white/70 mb-1">
                Desde
              </div>
              <div className="flex items-end gap-1">
                <span className="text-3xl md:text-4xl font-display font-bold">US$ 99,99</span>
                <span className="text-sm text-white/80 mb-1">/ mes</span>
              </div>
            </div>

            <ul className="space-y-2 text-sm md:text-base text-white/90 mb-8">
              {[
                <>
                  Marca y experiencia adaptada a la{' '}
                  <strong>identidad visual</strong> de tu institución.
                </>,
                <>
                  <strong>Entorno exclusivo</strong> para gestionar múltiples entrenadores y planteles.
                </>,
                <>
                  <strong>Panel centralizado</strong> para monitorear carga y salud de todos los deportistas.
                </>,
                <>
                  <strong>Acompañamiento</strong> en la implementación y soporte dedicado.
                </>,
              ].map((item, index) => (
                <li key={index} className="flex gap-2">
                  <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
                    <Check className="w-3 h-3 text-mas10-accent" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <motion.a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-white text-mas10-dark font-semibold text-sm md:text-base shadow-md hover:bg-mas10-accent transition-colors"
              >
                <WhatsAppIcon className="w-4 h-4 mr-2 text-emerald-500" />
                Consultar licencia institucional
              </motion.a>
              <p className="text-xs md:text-sm text-white/80">
                Envianos un mensaje y diseñamos juntos el plan ideal para tu institución.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;

